'use client';

import { c as _c } from "react/compiler-runtime";
import type { OnrampClient } from '@morfi/onramp-sdk';
import { useFundingActionRecovery, useFundingAuthentication, useOnrampSession } from '@morfi/onramp-sdk/react';
import { createMoonPayWebProvider, type MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useId } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { logger } from '@/lib/telemetry/logger';
import { FundingAuthenticationTerms } from '../checkout/funding-authentication-terms';
import { FundingLoading } from '../shared/funding-loading';
import { useFundingResource } from '../shared/use-funding-resource';
import { FundingCustomerVerificationForm } from '../verification/funding-customer-verification';
import { FundingRecoveryAuth } from '../verification/funding-recovery-auth';
export function FundingActionRecovery(t0) {
  const $ = _c(21);
  const {
    client,
    operationId,
    onBack
  } = t0;
  const t = useTranslations("onramp");
  const visit = useId();
  let t1;
  if ($[0] !== visit) {
    t1 = {
      navigationKey: visit
    };
    $[0] = visit;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const session = useOnrampSession(client, t1);
  const authentication = useFundingAuthentication(client);
  const current = authentication.session ?? session.data;
  let t2;
  let t3;
  if ($[2] !== session.error) {
    t2 = () => {
      if (session.error) {
        logger.warn("onramp.action_recovery_session_failed");
      }
    };
    t3 = [session.error];
    $[2] = session.error;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== t) {
    t4 = t("back");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onBack || $[8] !== t4) {
    t5 = <AnimatedButton variant="tertiary" onClick={onBack}>{t4}</AnimatedButton>;
    $[7] = onBack;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== authentication || $[11] !== client || $[12] !== current || $[13] !== onBack || $[14] !== operationId || $[15] !== session || $[16] !== t) {
    t6 = session.isPending ? <FundingLoading /> : session.isError ? <><p role="alert">{t("provider_unavailable")}</p><AnimatedButton onClick={() => {
        session.refetch();
      }}>{t("retry")}</AnimatedButton></> : current ? <RecoveryProvider key={current.sessionToken} client={client} operationId={operationId} sessionToken={current.sessionToken} authentication={authentication} onBack={onBack} /> : null;
    $[10] = authentication;
    $[11] = client;
    $[12] = current;
    $[13] = onBack;
    $[14] = operationId;
    $[15] = session;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <div className="rr-block flex flex-col gap-4">{t5}{t6}</div>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
function RecoveryProvider(props) {
  const $ = _c(5);
  let t0;
  if ($[0] !== props.sessionToken) {
    t0 = () => createMoonPayWebProvider({
      sessionToken: props.sessionToken,
      theme: {
        appearance: "dark"
      }
    });
    $[0] = props.sessionToken;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const provider = useFundingResource(t0);
  let t1;
  if ($[2] !== props || $[3] !== provider) {
    t1 = provider ? <RecoveryBody {...props} provider={provider} /> : <FundingLoading />;
    $[2] = props;
    $[3] = provider;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function RecoveryBody(t0) {
  const $ = _c(39);
  const {
    client,
    provider,
    operationId,
    authentication,
    onBack
  } = t0;
  const t = useTranslations("onramp");
  const recovery = useFundingActionRecovery(client, provider, operationId);
  let t1;
  if ($[0] !== onBack || $[1] !== recovery.data?.state) {
    t1 = () => {
      if (recovery.data?.state === "complete") {
        onBack();
      }
    };
    $[0] = onBack;
    $[1] = recovery.data?.state;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = recovery.data?.state;
  let t3;
  if ($[3] !== onBack || $[4] !== t2) {
    t3 = [t2, onBack];
    $[3] = onBack;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t1, t3);
  let t4;
  let t5;
  if ($[6] !== recovery.error) {
    t4 = () => {
      if (recovery.error) {
        logger.warn("onramp.action_recovery_failed");
      }
    };
    t5 = [recovery.error];
    $[6] = recovery.error;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== recovery) {
    t6 = () => {
      recovery.refetch();
    };
    $[9] = recovery;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const refresh = t6;
  if (recovery.isPending || recovery.isFetching) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <FundingLoading />;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  if (recovery.isError) {
    let t7;
    if ($[12] !== t) {
      t7 = t("provider_unavailable");
      $[12] = t;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== t7) {
      t8 = <p role="alert">{t7}</p>;
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t) {
      t9 = t("retry");
      $[16] = t;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== refresh || $[19] !== t9) {
      t10 = <AnimatedButton onClick={refresh}>{t9}</AnimatedButton>;
      $[18] = refresh;
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== t10 || $[22] !== t8) {
      t11 = <div className="flex flex-col gap-3">{t8}{t10}</div>;
      $[21] = t10;
      $[22] = t8;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    return t11;
  }
  const result = recovery.data;
  if (result?.state === "terms") {
    let t7;
    if ($[24] !== authentication.acceptTerms || $[25] !== authentication.busy || $[26] !== authentication.error || $[27] !== client) {
      t7 = <FundingAuthenticationTerms client={client} busy={authentication.busy} error={authentication.error} onAccept={authentication.acceptTerms} />;
      $[24] = authentication.acceptTerms;
      $[25] = authentication.busy;
      $[26] = authentication.error;
      $[27] = client;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    return t7;
  }
  if (result?.state === "connect") {
    let t7;
    if ($[29] !== provider || $[30] !== refresh || $[31] !== result.mismatch) {
      t7 = <FundingRecoveryAuth provider={provider} mismatch={result.mismatch} onComplete={refresh} />;
      $[29] = provider;
      $[30] = refresh;
      $[31] = result.mismatch;
      $[32] = t7;
    } else {
      t7 = $[32];
    }
    return t7;
  }
  if (result?.state === "verification") {
    let t7;
    if ($[33] !== client || $[34] !== provider || $[35] !== refresh || $[36] !== result.verification) {
      t7 = <FundingCustomerVerificationForm key={result.verification.customerId} client={client} provider={provider} verification={result.verification} onComplete={refresh} />;
      $[33] = client;
      $[34] = provider;
      $[35] = refresh;
      $[36] = result.verification;
      $[37] = t7;
    } else {
      t7 = $[37];
    }
    return t7;
  }
  let t7;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <FundingLoading />;
    $[38] = t7;
  } else {
    t7 = $[38];
  }
  return t7;
}